import { useProfanityCheck } from "@/hooks/useProfanityCheck";
import { cn } from "@/lib/utils";
import { AnonymousProfile } from "@/utils/anonymousProfile";
import Avatar from "boring-avatars";
import { AlertTriangleIcon, RefreshCwIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

interface CommentUser {
    id: string;
    name: string;
    profileImg: string;
}

interface CommentInputProps {
    isLoggedIn: boolean;
    isAnonymousMode: boolean;
    anonymousProfile: AnonymousProfile | null;
    user: CommentUser | null;
    isSubmitting: boolean;
    voteId: string; // voteId 추가하여 특정 투표 카드 식별
    onSubmit: (text: string) => Promise<void>;
    onRegenerateProfile: () => void;
    onSignIn: () => void;
}

/**
 * 댓글 입력 컴포넌트
 * 프로필 미리보기 및 익명 프로필 랜덤화 버튼 포함
 */
export const CommentInput: React.FC<CommentInputProps> = ({
    isLoggedIn,
    isAnonymousMode,
    anonymousProfile,
    user,
    isSubmitting,
    voteId,
    onSubmit,
    onRegenerateProfile,
    onSignIn
}) => {
    const [commentText, setCommentText] = useState("");
    const [isSubmittingAnimation, setIsSubmittingAnimation] = useState(false);
    const [isEntered, setIsEntered] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // 클라이언트 비속어 체크 (경고 UX용)
    const { hasProfanity } = useProfanityCheck(commentText);

    // 마운트 시 entering 애니메이션 트리거 및 포커스
    useEffect(() => {
        // 약간의 딜레이 후 애니메이션 시작 (브라우저 렌더링 대기)
        const timer = setTimeout(() => {
            setIsEntered(true);
            // 애니메이션 완료 후 textarea 포커스 (스크롤 없이)
            setTimeout(() => {
                textareaRef.current?.focus({ preventScroll: true });
            }, 200);
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    // 로그인 후 pending comment 복원
    useEffect(() => {
        if (isLoggedIn) {
            const restorePendingComment = async () => {
                const { getPendingComment, clearPendingComment } =
                    await import("@/utils/anonymousProfile");
                const pending = getPendingComment(voteId);
                if (pending) {
                    setCommentText(pending);
                    clearPendingComment(voteId);
                    // 복원 후 포커스 (스크롤 없이)
                    setTimeout(() => {
                        textareaRef.current?.focus({ preventScroll: true });
                    }, 100);
                }
            };
            restorePendingComment();
        }
    }, [isLoggedIn, voteId]);

    const handleSubmit = useCallback(async () => {
        if (!isLoggedIn) {
            // 로그인하지 않은 경우, 댓글을 localStorage에 저장
            if (commentText.trim()) {
                const { savePendingComment } =
                    await import("@/utils/anonymousProfile");
                savePendingComment(voteId, commentText);
            }
            onSignIn();
            return;
        }

        if (!commentText.trim() || isSubmitting) return;

        setIsSubmittingAnimation(true);
        try {
            await onSubmit(commentText);
            setCommentText("");
            setTimeout(() => {
                setIsSubmittingAnimation(false);
            }, 300);
        } catch (error) {
            setIsSubmittingAnimation(false);
        }
    }, [commentText, isLoggedIn, isSubmitting, voteId, onSubmit, onSignIn]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit]
    );

    return (
        <div
            className={cn(
                "mb-6 transition-all duration-300",
                isEntered
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-3 opacity-0"
            )}
            style={{
                transitionTimingFunction: "cubic-bezier(0.33, 1, 0.68, 1)"
            }}
        >
            {isLoggedIn && (
                <div className="flex items-center gap-2 p-2">
                    {isAnonymousMode ? (
                        // 익명 모드: 프로필이 있으면 표시, 없으면 아무것도 표시 안 함
                        anonymousProfile ? (
                            <div
                                className="flex w-full items-center
                                    justify-between"
                            >
                                <div
                                    key={`${anonymousProfile.avatarSeed}-${anonymousProfile.nickname}`}
                                    className="animate-profile-change flex
                                        items-center gap-2"
                                >
                                    <Avatar
                                        name={anonymousProfile.avatarSeed}
                                        variant="beam"
                                        size={32}
                                        className="select-none"
                                    />
                                    <span
                                        className="text-sm font-medium
                                            text-gray-700 dark:text-gray-300"
                                    >
                                        {anonymousProfile.nickname}
                                    </span>
                                    <span
                                        className="text-xs text-gray-500
                                            dark:text-gray-400"
                                    >
                                        (익명)
                                    </span>
                                </div>

                                <button
                                    onClick={onRegenerateProfile}
                                    className="flex items-center gap-2
                                        rounded-lg bg-gray-200 px-4 py-2 text-sm
                                        font-medium text-gray-700 transition-all
                                        duration-150 hover:bg-gray-200
                                        active:scale-95 dark:bg-gray-700
                                        dark:text-gray-300
                                        dark:hover:bg-gray-600"
                                >
                                    <RefreshCwIcon className="size-4" />
                                    프로필 새로 만들기
                                </button>
                            </div>
                        ) : null
                    ) : (
                        // 실명 모드: 사용자 프로필 표시
                        <>
                            {user?.profileImg ? (
                                <Image
                                    src={user.profileImg}
                                    alt={user.name}
                                    className="rounded-full object-cover"
                                    width={32}
                                    height={32}
                                />
                            ) : (
                                <div
                                    className="flex h-8 w-8 items-center
                                        justify-center rounded-full bg-gray-300
                                        text-sm font-semibold text-gray-600
                                        dark:bg-gray-700 dark:text-gray-300"
                                >
                                    {user?.name?.charAt(0)}
                                </div>
                            )}
                            <span
                                className="text-sm font-medium text-gray-700
                                    dark:text-gray-300"
                            >
                                {user?.name}
                            </span>
                        </>
                    )}
                </div>
            )}

            <div
                className={cn(
                    "flex transition-all duration-300",
                    isSubmittingAnimation && "animate-comment-submit"
                )}
            >
                <textarea
                    ref={textareaRef}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                        isLoggedIn
                            ? "댓글을 입력하세요 (Enter로 등록, Shift+Enter로 줄바꿈)"
                            : "댓글을 입력하려면 로그인하세요 (Enter로 로그인 페이지로 이동)"
                    }
                    disabled={isSubmitting}
                    rows={2}
                    className={cn(
                        `w-full resize-none rounded-lg border px-4 py-3 text-sm
                        text-black transition-all duration-300 ease-in-out
                        focus:outline-none disabled:bg-gray-50
                        disabled:text-gray-400 dark:text-white
                        dark:disabled:bg-gray-800 dark:disabled:text-gray-500`,
                        hasProfanity
                            ? `border-amber-400 bg-amber-50
                                focus:border-amber-500 dark:border-amber-600
                                dark:bg-amber-900/20`
                            : `border-gray-200 bg-gray-100 focus:border-gray-500
                                dark:border-gray-700 dark:bg-[#303030]`
                    )}
                />
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={cn(
                        `whitespace-nowrap rounded-lg bg-gray-900 py-3 text-sm
                        font-bold text-white transition-all duration-300
                        ease-in-out hover:-translate-y-0.5 hover:shadow-md
                        hover:shadow-gray-500/40 disabled:cursor-not-allowed
                        disabled:bg-gray-300 disabled:shadow-none dark:bg-white
                        dark:text-black dark:disabled:bg-gray-900`,
                        commentText.trim() === ""
                            ? `flex-0 pointer-events-none w-0 scale-0
                                overflow-hidden opacity-0`
                            : `pointer-events-auto ml-2 h-[66px] w-auto flex-1
                                px-5 py-3 opacity-100`
                    )}
                >
                    {isSubmitting
                        ? "등록 중..."
                        : isLoggedIn
                          ? "등록"
                          : "로그인"}
                </button>
            </div>

            {/* 비속어 경고 메시지 */}
            {hasProfanity && (
                <div
                    className="mt-2 flex items-center gap-2 rounded-lg
                        bg-amber-50 px-3 py-2 text-sm text-amber-700
                        dark:bg-amber-900/30 dark:text-amber-300"
                >
                    <AlertTriangleIcon className="size-4 flex-shrink-0" />
                    <span>
                        부적절한 표현이 포함되어 있습니다. 등록 시 일부 내용이
                        가려질 수 있습니다.
                    </span>
                </div>
            )}
        </div>
    );
};
